import React, { useState } from 'react';
import { Search, Globe, CheckCircle, XCircle, AlertCircle, Clock, RefreshCw, ChevronDown, ChevronUp, Copy, Check, ExternalLink, Shield, Cloud } from 'lucide-react';

interface DomainStatusResult {
  success: boolean;
  domain: string;
  status?: string;
  exists?: boolean;
  nameservers?: string[];
  cloudflareZoneId?: string;
  redirectUrl?: string;
  message?: string;
  error?: string;
  rawData?: any;
  debugInfo?: {
    status?: number;
    statusText?: string;
    responseData?: any;
  };
}

interface ApiResponse {
  success: boolean;
  message: string;
  summary: {
    total: number;
    successful: number;
    failed: number;
    active?: number;
    pending?: number;
    notFound?: number;
  };
  results: DomainStatusResult[];
}

interface ProcessingStatus {
  domain: string;
  status: 'pending' | 'checking' | 'completed' | 'failed';
  currentStep?: string;
  result?: DomainStatusResult;
}

export const CheckDomainStatus: React.FC = () => {
  const [domainsInput, setDomainsInput] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [results, setResults] = useState<ApiResponse | null>(null);
  const [liveProcessing, setLiveProcessing] = useState<ProcessingStatus[]>([]);
  const [expandedDetails, setExpandedDetails] = useState<Set<string>>(new Set());
  const [expandedSummaryCategories, setExpandedSummaryCategories] = useState<Set<string>>(new Set());
  const [copiedCategory, setCopiedCategory] = useState<string | null>(null);
  const [copiedDomain, setCopiedDomain] = useState<string | null>(null);

  const getDomainsList = () => {
    return domainsInput
      .split('\n')
      .map(domain => domain.trim())
      .filter(domain => domain.length > 0);
  };

  const toggleDetails = (domain: string) => {
    const newExpanded = new Set(expandedDetails);
    if (newExpanded.has(domain)) {
      newExpanded.delete(domain);
    } else {
      newExpanded.add(domain);
    }
    setExpandedDetails(newExpanded);
  };

  const toggleSummaryCategory = (category: string) => {
    const newExpanded = new Set(expandedSummaryCategories);
    if (newExpanded.has(category)) {
      newExpanded.delete(category);
    } else {
      newExpanded.add(category);
    }
    setExpandedSummaryCategories(newExpanded);
  };

  const copyToClipboard = async (category: string) => {
    const domains = getDomainsByCategory(category);
    const domainList = domains.map(result => result.domain).join('\n');
    
    try {
      await navigator.clipboard.writeText(domainList);
      setCopiedCategory(category);
      setTimeout(() => setCopiedCategory(null), 2000);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  };

  const copyDomainToClipboard = async (domain: string) => {
    try {
      await navigator.clipboard.writeText(domain);
      setCopiedDomain(domain);
      setTimeout(() => setCopiedDomain(null), 2000);
    } catch (err) {
      console.error('Failed to copy domain to clipboard:', err);
    }
  };

  const copyAllDomainsToClipboard = async () => {
    if (!results) return;
    const allDomains = results.results.map(result => result.domain).join('\n');
    
    try {
      await navigator.clipboard.writeText(allDomains);
      setCopiedCategory('all-domains');
      setTimeout(() => setCopiedCategory(null), 2000);
    } catch (err) {
      console.error('Failed to copy all domains to clipboard:', err);
    }
  };

  const getDomainsByCategory = (category: string) => {
    if (!results) return [];
    
    switch (category) {
      case 'successful':
        return results.results.filter(r => r.success);
      case 'active':
        return results.results.filter(r => r.success && r.status === 'active');
      case 'pending':
        return results.results.filter(r => r.success && r.status === 'pending');
      case 'not-found':
        return results.results.filter(r => r.success && r.exists === false);
      case 'failed':
        return results.results.filter(r => !r.success);
      default:
        return [];
    }
  };

  const handleCheckDomainStatus = async () => {
    const domains = getDomainsList();
    
    if (domains.length === 0) {
      alert('Please enter at least one domain');
      return;
    }

    setIsProcessing(true);
    setResults(null);
    setLiveProcessing([]);
    setExpandedDetails(new Set());

    // Initialize processing status for each domain
    const initialProcessing = domains.map(domain => ({
      domain,
      status: 'pending' as const
    }));
    setLiveProcessing(initialProcessing);

    try {
      // Process domains one by one to show live updates
      const allResults: DomainStatusResult[] = [];
      
      for (let i = 0; i < domains.length; i++) {
        const domain = domains[i].trim();
        if (!domain) continue;

        // Update status to processing
        setLiveProcessing(prev => prev.map(item => 
          item.domain === domain 
            ? { ...item, status: 'checking', currentStep: 'Checking domain status...' }
            : item
        ));

        try {
          // Make API call for single domain
          const response = await fetch('/api/domains/check-status', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-API-Key': process.env.REACT_APP_API_KEY || ''
            },
            body: JSON.stringify({
              domains: [domain]
            })
          });

          if (response.status === 401) {
            throw new Error('API authentication failed. Please check if REACT_APP_API_KEY is configured correctly.');
          }

          const data = await response.json();
          
          if (response.ok && data.results && data.results.length > 0) {
            const result = data.results[0];
            allResults.push(result);
            
            // Update processing status with final result
            setLiveProcessing(prev => prev.map(item => 
              item.domain === domain 
                ? { 
                    ...item, 
                    status: result.success ? 'completed' : 'failed',
                    currentStep: result.success 
                      ? `Status: ${result.status || 'unknown'}${result.exists === false ? ' (not found)' : ''}`
                      : 'Failed to check status',
                    result: result
                  }
                : item
            ));
            
            // Expand details for completed domain
            setExpandedDetails(prev => new Set([...Array.from(prev), domain]));
          } else {
            // Handle error case
            const errorResult: DomainStatusResult = {
              success: false,
              domain: domain,
              error: data.message || 'Unknown error occurred'
            };
            allResults.push(errorResult);
            
            setLiveProcessing(prev => prev.map(item => 
              item.domain === domain 
                ? { 
                    ...item, 
                    status: 'failed',
                    currentStep: 'Failed to check status',
                    result: errorResult
                  }
                : item
            ));
          }
        } catch (error) {
          console.error(`Error checking status for domain ${domain}:`, error);
          const errorResult: DomainStatusResult = {
            success: false,
            domain: domain,
            error: 'Network error occurred'
          };
          allResults.push(errorResult);
          
          setLiveProcessing(prev => prev.map(item => 
            item.domain === domain 
              ? { 
                  ...item, 
                  status: 'failed',
                  currentStep: 'Network error',
                  result: errorResult
                }
              : item
          ));
        }

        // Small delay between domains to make processing visible
        if (i < domains.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }

      // Calculate final summary
      const successful = allResults.filter(r => r.success).length;
      const failed = allResults.filter(r => !r.success).length;
      const active = allResults.filter(r => r.success && r.status === 'active').length;
      const pending = allResults.filter(r => r.success && r.status === 'pending').length;
      const notFound = allResults.filter(r => r.success && r.exists === false).length;

      setResults({
        success: true,
        message: `Checked ${allResults.length} domains: ${successful} successful, ${failed} failed, ${active} active, ${pending} pending, ${notFound} not found`,
        summary: {
          total: allResults.length,
          successful: successful,
          failed: failed,
          active: active,
          pending: pending,
          notFound: notFound
        },
        results: allResults
      });

    } catch (error) {
      console.error('Error checking domain status:', error);
      alert('Failed to connect to the API. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const getStatusIcon = (status: ProcessingStatus['status']) => {
    switch (status) {
      case 'pending':
        return <Clock className="h-4 w-4 text-gray-400" />;
      case 'checking':
        return <RefreshCw className="h-4 w-4 text-blue-500 animate-spin" />;
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'failed':
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return <Clock className="h-4 w-4 text-gray-400" />;
    }
  };

  const getStatusColor = (status: ProcessingStatus['status']) => {
    switch (status) {
      case 'pending':
        return 'text-gray-500';
      case 'checking':
        return 'text-blue-600';
      case 'completed':
        return 'text-green-600';
      case 'failed':
        return 'text-red-600';
      default:
        return 'text-gray-500';
    }
  };

  const getDomainStatusIcon = (result: DomainStatusResult) => {
    if (!result.success) {
      return <XCircle className="h-4 w-4 text-red-500" />;
    }
    
    if (result.exists === false) {
      return <AlertCircle className="h-4 w-4 text-yellow-500" />;
    }
    
    switch (result.status) {
      case 'active':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'pending':
        return <Clock className="h-4 w-4 text-orange-500" />;
      default:
        return <Globe className="h-4 w-4 text-blue-500" />;
    }
  };

  const getDomainStatusColor = (result: DomainStatusResult) => {
    if (!result.success) {
      return 'text-red-600';
    }
    
    if (result.exists === false) {
      return 'text-yellow-600';
    }
    
    switch (result.status) {
      case 'active':
        return 'text-green-600';
      case 'pending':
        return 'text-orange-600';
      default:
        return 'text-blue-600';
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Check Domain Status</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Check the status of multiple domains in Cloudflare with live processing view.
        </p>
      </div>

      <div className="card-modern p-6 space-y-6">
        <div>
          <h2 className="text-lg font-semibold mb-4">Domain List</h2>
          <div>
            <label className="text-sm font-medium mb-2 block">
              Domain Names (one per line)
            </label>
            <textarea
              value={domainsInput}
              onChange={(e) => setDomainsInput(e.target.value)}
              className="input-modern w-full px-3 py-2 text-sm min-h-[120px] resize-y"
              placeholder="example1.com&#10;example2.com&#10;example3.com"
              rows={6}
              disabled={isProcessing}
            />
            <p className="text-xs text-muted-foreground mt-2">
              {getDomainsList().length} domain{getDomainsList().length !== 1 ? 's' : ''} entered
            </p>
          </div>
        </div>

        <div className="flex items-center justify-end pt-4">
          <button 
            onClick={handleCheckDomainStatus}
            disabled={!domainsInput.trim() || isProcessing}
            className="btn-primary px-6 py-2 rounded-lg font-medium flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isProcessing ? (
              <RefreshCw className="h-4 w-4 animate-spin" />
            ) : (
              <Search className="h-4 w-4" />
            )}
            <span>
              {isProcessing 
                ? 'Checking...' 
                : `Check Status${getDomainsList().length > 1 ? ' for All' : ''}`
              }
            </span>
          </button>
        </div>
      </div>

      {/* Live Processing Section */}
      {liveProcessing.length > 0 && (
        <div className="card-modern p-6 space-y-4">
          <h2 className="text-lg font-semibold">Processing Status</h2>
          <div className="space-y-3">
            {liveProcessing.map((item, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                <div className="flex items-center space-x-3">
                  {getStatusIcon(item.status)}
                  <div>
                    <div className="font-medium text-sm">{item.domain}</div>
                    <div className={`text-xs ${getStatusColor(item.status)}`}>
                      {item.currentStep || 'Waiting...'}
                    </div>
                  </div>
                </div>
                
                {item.result && (
                  <div className="flex items-center space-x-2">
                    {getDomainStatusIcon(item.result)}
                    <span className={`text-xs font-medium ${getDomainStatusColor(item.result)}`}>
                      {item.result.success 
                        ? (item.result.exists === false ? 'Not Found' : (item.result.status || 'Unknown').toUpperCase())
                        : 'Failed'
                      }
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Results Section */}
      {results && (
        <div className="space-y-6">
          {/* Summary */}
          <div className="card-modern p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Summary</h2>
              <div className="text-sm text-muted-foreground">
                {results.summary.total} domains processed
              </div>
            </div>
            
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                <div className="flex items-center space-x-2 mb-2">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  <span className="font-medium text-green-700 dark:text-green-300">Active</span>
                </div>
                <div className="text-2xl font-bold text-green-700 dark:text-green-300">
                  {results.summary.active || 0}
                </div>
              </div>
              
              <div className="bg-orange-50 dark:bg-orange-900/20 p-4 rounded-lg">
                <div className="flex items-center space-x-2 mb-2">
                  <Clock className="h-4 w-4 text-orange-500" />
                  <span className="font-medium text-orange-700 dark:text-orange-300">Pending</span>
                </div>
                <div className="text-2xl font-bold text-orange-700 dark:text-orange-300">
                  {results.summary.pending || 0}
                </div>
              </div>
              
              <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg">
                <div className="flex items-center space-x-2 mb-2">
                  <AlertCircle className="h-4 w-4 text-yellow-500" />
                  <span className="font-medium text-yellow-700 dark:text-yellow-300">Not Found</span>
                </div>
                <div className="text-2xl font-bold text-yellow-700 dark:text-yellow-300">
                  {results.summary.notFound || 0}
                </div>
              </div>
              
              <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg">
                <div className="flex items-center space-x-2 mb-2">
                  <XCircle className="h-4 w-4 text-red-500" />
                  <span className="font-medium text-red-700 dark:text-red-300">Failed</span>
                </div>
                <div className="text-2xl font-bold text-red-700 dark:text-red-300">
                  {results.summary.failed}
                </div>
              </div>
              
              <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                <div className="flex items-center space-x-2 mb-2">
                  <CheckCircle className="h-4 w-4 text-blue-500" />
                  <span className="font-medium text-blue-700 dark:text-blue-300">Successful</span>
                </div>
                <div className="text-2xl font-bold text-blue-700 dark:text-blue-300">
                  {results.summary.successful}
                </div>
              </div>
              
              <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
                <div className="flex items-center space-x-2 mb-2">
                  <Globe className="h-4 w-4 text-gray-500" />
                  <span className="font-medium text-gray-700 dark:text-gray-300">Total</span>
                </div>
                <div className="text-2xl font-bold text-gray-700 dark:text-gray-300">
                  {results.summary.total}
                </div>
              </div>
            </div>

            {/* Category Details */}
            <div className="mt-6 space-y-3">
              {[
                { key: 'active', label: 'Active Domains', count: results.summary.active || 0, color: 'green' },
                { key: 'pending', label: 'Pending Domains', count: results.summary.pending || 0, color: 'orange' },
                { key: 'not-found', label: 'Not Found Domains', count: results.summary.notFound || 0, color: 'yellow' },
                { key: 'failed', label: 'Failed to Check', count: results.summary.failed, color: 'red' }
              ].filter(category => category.count > 0).map(category => (
                <div key={category.key} className="border border-border rounded-lg p-3">
                  <button
                    onClick={() => toggleSummaryCategory(category.key)}
                    className="flex items-center justify-between w-full text-left"
                  >
                    <span className="font-medium">
                      {category.label} ({category.count})
                    </span>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          copyToClipboard(category.key);
                        }}
                        className="text-sm text-primary hover:text-primary/80 flex items-center space-x-1"
                      >
                        {copiedCategory === category.key ? (
                          <Check className="h-3 w-3" />
                        ) : (
                          <Copy className="h-3 w-3" />
                        )}
                      </button>
                      {expandedSummaryCategories.has(category.key) ? (
                        <ChevronUp className="h-4 w-4" />
                      ) : (
                        <ChevronDown className="h-4 w-4" />
                      )}
                    </div>
                  </button>
                  
                  {expandedSummaryCategories.has(category.key) && (
                    <div className="mt-3 space-y-1">
                      {getDomainsByCategory(category.key).map(result => (
                        <div key={result.domain} className="flex items-center justify-between text-sm p-2 bg-muted rounded">
                          <span className="font-mono">{result.domain}</span>
                          <span className={`font-medium ${getDomainStatusColor(result)}`}>
                            {result.success 
                              ? (result.exists === false ? 'Not Found' : (result.status || 'Unknown').toUpperCase())
                              : 'Failed'
                            }
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Detailed Results */}
          <div className="card-modern p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">Detailed Results</h2>
              <button
                onClick={copyAllDomainsToClipboard}
                className="flex items-center space-x-2 px-3 py-2 text-sm bg-muted hover:bg-muted/80 rounded-lg transition-colors"
                title="Copy all domains to clipboard"
              >
                {copiedCategory === 'all-domains' ? (
                  <Check className="h-4 w-4 text-green-500" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
                <span>Copy All Domains</span>
              </button>
            </div>
            <div className="space-y-4">
              {results.results.map((result, index) => (
                <div key={index} className="border border-border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      {getDomainStatusIcon(result)}
                      <div>
                        <div className="flex items-center space-x-2">
                          <div className="font-medium text-lg">{result.domain}</div>
                          <button
                            onClick={() => copyDomainToClipboard(result.domain)}
                            className="p-1 hover:bg-muted rounded transition-colors"
                            title="Copy domain to clipboard"
                          >
                            {copiedDomain === result.domain ? (
                              <Check className="h-3 w-3 text-green-500" />
                            ) : (
                              <Copy className="h-3 w-3 text-muted-foreground hover:text-foreground" />
                            )}
                          </button>
                        </div>
                        <div className={`text-sm ${getDomainStatusColor(result)}`}>
                          {result.success 
                            ? (result.exists === false 
                                ? 'Domain not found in Cloudflare' 
                                : `Status: ${(result.status || 'unknown').toUpperCase()}`)
                            : `Error: ${result.error}`
                          }
                        </div>
                      </div>
                    </div>
                    
                    <button
                      onClick={() => toggleDetails(result.domain)}
                      className="text-sm text-primary hover:text-primary/80 flex items-center space-x-1"
                    >
                      <span>Details</span>
                      {expandedDetails.has(result.domain) ? (
                        <ChevronUp className="h-3 w-3" />
                      ) : (
                        <ChevronDown className="h-3 w-3" />
                      )}
                    </button>
                  </div>

                  {expandedDetails.has(result.domain) && (
                    <div className="ml-7 space-y-3 pt-3 border-t border-border">
                      {result.success && (
                        <div className="grid gap-4 md:grid-cols-2">
                          {result.cloudflareZoneId && (
                            <div>
                              <span className="text-sm font-medium text-muted-foreground">Cloudflare Zone ID:</span>
                              <div className="font-mono text-xs bg-muted px-2 py-1 rounded mt-1">
                                {result.cloudflareZoneId}
                              </div>
                            </div>
                          )}
                          
                          {result.redirectUrl && (
                            <div>
                              <span className="text-sm font-medium text-muted-foreground">Redirect URL:</span>
                              <div className="text-sm text-blue-600 truncate mt-1">
                                <a href={result.redirectUrl} target="_blank" rel="noopener noreferrer" className="flex items-center space-x-1">
                                  <span>{result.redirectUrl}</span>
                                  <ExternalLink className="h-3 w-3" />
                                </a>
                              </div>
                            </div>
                          )}
                          
                          {result.nameservers && result.nameservers.length > 0 && (
                            <div className="md:col-span-2">
                              <span className="text-sm font-medium text-muted-foreground">Nameservers:</span>
                              <div className="mt-1 space-y-1">
                                {result.nameservers.map((ns, nsIndex) => (
                                  <div key={nsIndex} className="font-mono text-xs bg-muted px-2 py-1 rounded">
                                    {ns}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {result.message && (
                            <div className="md:col-span-2">
                              <span className="text-sm font-medium text-muted-foreground">Message:</span>
                              <div className="text-sm mt-1">{result.message}</div>
                            </div>
                          )}
                        </div>
                      )}
                      
                      {result.debugInfo && (
                        <div>
                          <span className="text-sm font-medium text-muted-foreground">Debug Info:</span>
                          <div className="mt-1 p-2 bg-red-50 dark:bg-red-900/20 rounded text-xs">
                            <p><strong>Status:</strong> {result.debugInfo.status} {result.debugInfo.statusText}</p>
                            {result.debugInfo.responseData && (
                              <div className="mt-1">
                                <strong>Response:</strong>
                                <pre className="mt-1 p-2 bg-red-100 dark:bg-red-800/30 rounded text-xs overflow-x-auto">
                                  {JSON.stringify(result.debugInfo.responseData, null, 2)}
                                </pre>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {result.rawData && (
                        <details className="text-xs">
                          <summary className="cursor-pointer text-muted-foreground">
                            Raw API Response
                          </summary>
                          <pre className="mt-2 p-3 bg-muted rounded overflow-x-auto">
                            {JSON.stringify(result.rawData, null, 2)}
                          </pre>
                        </details>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}; 